import { useQuery } from "@tanstack/react-query";
import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";

interface VersionInfo {
  version: string;
  build: string | null;
}

function useVersion() {
  return useQuery({
    queryKey: ["app-version"],
    queryFn: async () => (await api.get<VersionInfo>("/version")).data,
    staleTime: Infinity, // verandert alleen bij een nieuwe deploy
  });
}

export default function Layout({ children }: { children: ReactNode }) {
  const loc = useLocation();
  const version = useVersion();
  const auth = useAuth();
  const roleLabel: Record<string, string> = {
    hdm: "KBS-beheer",
    corporatie: "Corporatie",
    comaker: "Co-maker",
  };
  const semver = version.data?.version;
  const build = version.data?.build;
  const navItems = [
    { to: "/", label: "Dashboard" },
    { to: "/genereer", label: "Rapport genereren" },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-kbs-light">
      <header className="border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded bg-kbs-primary px-2.5 py-1 text-sm font-bold text-white">KBS</div>
            <div>
              <div className="text-base font-bold text-kbs-primary">AI Report Generator</div>
              <div className="text-xs text-slate-500">{semver ? `v${semver}` : ""}</div>
            </div>
          </div>
          <nav className="flex items-center gap-2">
            {navItems.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className={`rounded px-3 py-1.5 text-sm font-medium ${
                  loc.pathname === n.to
                    ? "bg-kbs-primary text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {n.label}
              </Link>
            ))}
            {auth.user ? (
              <div className="ml-4 flex items-center gap-2 border-l border-slate-200 pl-4 text-xs text-slate-500">
                <div className="text-right">
                  <div className="font-medium text-slate-700">{auth.user.display_name || auth.user.email}</div>
                  <div>{roleLabel[auth.user.role] ?? auth.user.role}</div>
                </div>
                {auth.canLogout ? (
                  <button
                    type="button"
                    onClick={auth.logout}
                    className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                  >
                    Uitloggen
                  </button>
                ) : null}
              </div>
            ) : null}
          </nav>
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-8">{children}</main>
      <footer className="border-t border-slate-200 bg-white px-6 py-3 text-center text-xs text-slate-400">
        Universal.cloud — KBS AI Report{semver ? ` v${semver}` : ""}
        {build ? <span className="ml-1 text-slate-300">(build {build})</span> : null}
      </footer>
    </div>
  );
}
